import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import './i18n';

// Layout
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';

// Components
import ScrollToTop from './components/ScrollToTop';

// Auth
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Public Pages
import Home from './pages/Home';
import Activities from './pages/Activities';
import ActivityDetails from './pages/ActivityDetails';
import SubscriptionPlans from './pages/SubscriptionPlans';
import Contact from './pages/Contact';
import MyActivities from './pages/MyActivities';
import ActivityCategory from './pages/ActivityCategory';

// User Pages
import UserDashboard from './pages/user/Dashboard';
import UserProfile from './pages/user/Profile';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import ActivityManagement from './pages/admin/ActivityManagement';
import NewsletterManagement from './pages/admin/NewsletterManagement';
import PlanManagement from './pages/admin/PlanManagement';
import AdminPanel from './pages/admin/AdminPanel';
import Statistics from './pages/admin/Statistics';
import ActivityTypeManagement from './pages/admin/ActivityTypeManagement';

const ProtectedRoute = ({ children, roles = ['user'] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} />;
  }

  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const App = () => {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <Routes>
              {/* Public Routes */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/activities" element={<Activities />} />
                <Route path="/activities/category/:category" element={<Activities />} />
                <Route path="/activities/category-info/:category" element={<ActivityCategory />} />
                <Route path="/activities/:id" element={<ActivityDetails />} />
                <Route path="/subscriptions" element={<SubscriptionPlans />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/my-activities" element={<MyActivities />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
              </Route>

              {/* Protected User Routes */}
              <Route
                element={
                  <ProtectedRoute roles={['user', 'admin']}>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="/dashboard"
                  element={<UserDashboard />}
                />
                <Route
                  path="/profile"
                  element={<UserProfile />}
                />
              </Route>

              {/* Protected Admin Routes */}
              <Route
                element={
                  <ProtectedRoute roles={['admin']}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="/admin"
                  element={<Navigate to="/admin/dashboard" replace />}
                />
                <Route
                  path="/admin/dashboard"
                  element={<AdminDashboard />}
                />
                <Route
                  path="/admin/plans"
                  element={<PlanManagement />}
                />
                <Route
                  path="/admin/statistics"
                  element={<Statistics />}
                />
                <Route
                  path="/admin/activities"
                  element={<ActivityManagement />}
                />
                <Route
                  path="/admin/activity-types"
                  element={<ActivityTypeManagement />}
                />
                <Route
                  path="/admin/newsletters"
                  element={<NewsletterManagement />}
                />
                <Route
                  path="/admin/users"
                  element={<Navigate to="/admin/panel" replace />}
                />
                <Route
                  path="/admin/panel"
                  element={<AdminPanel />}
                />
              </Route>

              {/* 404 Route */}
              <Route
                path="*"
                element={
                  <MainLayout>
                    <Navigate to="/" replace />
                  </MainLayout>
                }
              />
            </Routes>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
